import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../utils/api";
import styles from "./EditAppAccessLevelPage.module.scss";
import CustomInput from "../../components/Inputs/CustomInput";
import Modal from "../../components/Modal/Modal";
import { AxiosResponse, AxiosErrorType } from "../../types";

interface FormValues {
  name: string;
  permissions: string;
}

const EditAppAccessLevelPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [formValues, setFormValues] = useState<FormValues>({
    name: "",
    permissions: "{}",
  });
  const [modalMessage, setModalMessage] = useState<string>("");
  const [modalIsError, setModalIsError] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!id || isNaN(parseInt(id))) {
        console.error("[EditAppAccessLevelPage] Invalid access level ID:", id);
        setModalMessage("Invalid access level ID");
        setModalIsError(true);
        setShowModal(true);
        setLoading(false);
        return;
      }

      try {
        console.log("[EditAppAccessLevelPage] Fetching access level for ID:", id);
        const response = await api.post<AxiosResponse<{ access_levels: { id: number; name: string; permissions: Record<string, boolean> }[] }>>(
          "/core/role/access-level/get",
          {}
        );
        console.log("[EditAppAccessLevelPage] API response:", response.data);
        const accessLevelData = response.data.access_levels?.find((al) => al.id === parseInt(id));
        if (!accessLevelData) {
          console.error("[EditAppAccessLevelPage] App access level not found for id:", id);
          throw new Error("App access level not found");
        }
        setFormValues({
          name: accessLevelData.name,
          permissions: JSON.stringify(accessLevelData.permissions || {}, null, 2),
        });
        console.log("[EditAppAccessLevelPage] Fetched access level:", accessLevelData);
      } catch (err: AxiosErrorType) {
        const errorMessage = err.response?.data?.message || err.message || "Failed to fetch app access level";
        console.error("[EditAppAccessLevelPage] Fetch failed:", err.message, err.response?.data, err.response?.status);
        setModalMessage(errorMessage);
        setModalIsError(true);
        setShowModal(true);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleInputChange = (name: string, value: string) => {
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePermissionsKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const input = e.target as HTMLInputElement;
      const value = input.value;
      const cursorPos = input.selectionStart || 0;
      if (cursorPos > 0) {
        const newValue = value.slice(0, cursorPos - 1) + value.slice(cursorPos);
        setFormValues((prev) => ({
          ...prev,
          permissions: newValue,
        }));
      }
    }
  };

  const handleSubmit = async () => {
    if (!formValues.name) {
      setModalMessage("Please provide a name for the access level.");
      setModalIsError(true);
      setShowModal(true);
      return;
    }

    let permissionsObj: Record<string, boolean> = {};
    try {
      permissionsObj = JSON.parse(formValues.permissions);
      if (typeof permissionsObj !== "object" || Array.isArray(permissionsObj)) {
        throw new Error("Permissions must be a valid JSON object.");
      }
    } catch (err) {
      setModalMessage("Invalid permissions format. Please provide a valid JSON object (e.g., {\"read\": true, \"write\": false}).");
      setModalIsError(true);
      setShowModal(true);
      return;
    }

    setLoading(true);
    try {
      console.log("[EditAppAccessLevelPage] Updating access level:", { access_level_id: id, name: formValues.name, permissions: permissionsObj });
      const response = await api.post("/core/role/access-level/update", {
        access_level_id: parseInt(id || "0"),
        name: formValues.name,
        permissions: permissionsObj,
      });
      console.log("[EditAppAccessLevelPage] Access level updated:", response.data);
      setModalMessage("Access level updated successfully.");
      setModalIsError(false);
      setShowModal(true);
      setTimeout(() => navigate("/roles"), 2000);
    } catch (err: AxiosErrorType) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to update app access level";
      console.error("[EditAppAccessLevelPage] Update failed:", err.message, err.response?.data, err.response?.status);
      setModalMessage(errorMessage);
      setModalIsError(true);
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className={styles.loading}>Loading...</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.title}>Edit App Access Level</div>
      {modalMessage && (
        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          message={modalMessage}
          isError={modalIsError}
        />
      )}
      <div className={styles.section}>
        <div className={styles.formGroup}>
          <CustomInput
            label="Access Level Name"
            value={formValues.name}
            onChange={(val: string) => handleInputChange("name", val)}
            type="text"
            placeholder="e.g., Admin"
            size="full"
            required
          />
          <CustomInput
            label="Permissions (JSON)"
            value={formValues.permissions}
            onChange={(val: string) => handleInputChange("permissions", val)}
            onKeyDown={handlePermissionsKeyDown}
            type="text"
            placeholder='e.g., {"read": true, "write": false}'
            size="full"
          />
        </div>
        <div className={styles.actions}>
          <button
            onClick={handleSubmit}
            className={styles.actionButton}
            disabled={loading}
          >
            {loading ? "Updating..." : "Update"}
          </button>
          <button
            onClick={() => navigate("/roles")}
            className={styles.actionButton}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditAppAccessLevelPage;
